export type AutomotiveVariantKey = "general" | "serviceCenters" | "dealers" | "gtaDealers" | "marine" | "powerSports";

export interface AutomotiveProblemSolution {
  problem: string;
  solution: string;
  description: string;
}

export interface AutomotiveFeature {
  title: string;
  description: string;
  href?: string;
}

export interface AutomotiveOfferCard {
  id: string;
  name: string;
  tagline: string;
  description: string;
  audience: string;
  bestFor?: string;
  setupFee?: string;
  monthly?: string;
  price: string;
  billing: string;
  note?: string;
  valueProps: string[];
  primaryCtaLabel?: string;
  primaryCtaHref?: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
}

export interface AutomotiveFAQItem {
  question: string;
  answer: string;
}

export interface AutomotiveSystem {
  id: string;
  title: string;
  tagline: string;
  description: string;
  included: {
    title: string;
    description: string;
  }[];
  outcome: string;
}

export interface AutomotivePageContent {
  key: AutomotiveVariantKey;
  slug: string;
  meta: {
    title: string;
    description: string;
    canonical: string;
    keywords?: string;
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
  };
  hero: {
    eyebrow?: string;
    title: string;
    description: string;
    supporting?: string;
    audienceBullets?: string[];
    primaryCta: string;
    secondaryCta: string;
  };
  credibility: string;
  credibilityNotes?: string[];
  credibilityCard?: {
    title: string;
    stats: string;
    description?: string;
  };
  reviewWidget?: {
    heading: string;
    intro: string;
    rating: string;
    reviewCount: string;
    highlights: string[];
    ctaLabel: string;
    ctaHref: string;
    testimonials?: {
      quote: string;
      author: string;
      role?: string;
      date?: string;
    }[];
  };
  problems: AutomotiveProblemSolution[];
  systems?: AutomotiveSystem[];
  systemsIntro?: {
    eyebrow: string;
    title: string;
    description: string;
  };
  features: AutomotiveFeature[];
  offers: AutomotiveOfferCard[];
  proof: {
    eyebrow?: string;
    statement: string;
    supporting: string;
    linkLabel: string;
    linkHref: string;
  };
  faq: AutomotiveFAQItem[];
  stickyCta: {
    heading: string;
    secondary: string;
  };
  demo: {
    url: string;
    heading: string;
    description: string;
  };
  serviceSchemaName: string;
  subpages?: {
    title: string;
    description: string;
    href: string;
  }[];
  experience?: {
    heading: string;
    subheading?: string;
    intro?: string;
    bullets?: string[];
    stats?: {
      value: string;
      label: string;
      description?: string;
    }[];
  };
  scaleProof?: {
    heading: string;
    description?: string;
    bullets?: string[];
  };
  deepProblems?: {
    heading: string;
    items: {
      title: string;
      summary: string;
      bullets: string[];
    }[];
  };
  stack?: {
    heading: string;
    subheading?: string;
    rows: {
      component: string;
      purpose: string;
      result: string;
    }[];
  };
  recommendedRollout?: {
    heading: string;
    intro: string;
    note?: string;
    steps: {
      title: string;
      description: string;
    }[];
  };
  aiSupport?: {
    heading: string;
    description: string;
    bullets: string[];
  };
  addons?: {
    heading: string;
    subheading?: string;
    items: {
      title: string;
      description: string;
    }[];
    footer?: string;
  };
  resourcesLinks?: {
    label: string;
    description: string;
    href: string;
  }[];
  starterOffer?: {
    heading: string;
    description: string;
    price: string;
    note?: string;
    ctaLabel?: string;
    ctaHref?: string;
    chatbotWidget?: {
      widgetId: string;
      locationId: string;
    };
  };
  whyChoose?: {
    heading: string;
    bullets: string[];
    footer?: string;
    quotes?: {
      quote: string;
      name?: string;
      role?: string;
    }[];
  };
  closingNote?: {
    heading: string;
    description: string;
    primaryCtaLabel?: string;
    primaryCtaHref?: string;
    secondaryCtaLabel?: string;
    secondaryCtaHref?: string;
  };
  caseStudy?: {
    heading: string;
    intro?: string;
    context?: string;
    problems?: string[];
    solutions?: string[];
    outcomes?: string[];
  };
  comparison?: {
    eyebrow?: string;
    heading: string;
    intro?: string;
    toolsRunLine?: string;
    columns: { name: string; subtitle?: string; highlight?: boolean }[];
    rows: { capability: string; values: (boolean | "partial")[] }[];
    wedge?: string;
  };
  sixPillars?: {
    eyebrow?: string;
    heading: string;
    intro?: string;
    pillars: { number: string; title: string; tagline: string; points: string[] }[];
  };
  aiShowcase?: {
    eyebrow?: string;
    heading: string;
    intro?: string;
    capabilities: { title: string; description: string }[];
    proof?: { heading: string; points: string[]; linkLabel?: string; linkHref?: string };
  };
  inventoryEngine?: {
    eyebrow?: string;
    heading: string;
    intro?: string;
    features: { title: string; description: string }[];
    proof?: string;
    ctaLabel?: string;
    ctaHref?: string;
  };
  websiteSeo?: {
    eyebrow?: string;
    heading: string;
    intro?: string;
    tracks: { badge: string; title: string; tagline: string; points: string[]; footnote?: string }[];
    proof?: string;
  };
  tiers?: {
    eyebrow?: string;
    heading: string;
    intro?: string;
    note?: string;
    ctaLabel?: string;
    ctaHref?: string;
    tiers: { name: string; tagline: string; turnsOn: string[]; carriesOver?: string; highlight?: boolean }[];
  };
}

const BASE_DEMO_URL = "https://www.loom.com/embed/your-frayze-automotive-demo";
export const AUTOMOTIVE_BOOKING_URL = "https://api.frayze.ca/widget/booking/sJpmHE1UUjYl00tZdlot";

const baseOffers: AutomotiveOfferCard[] = [
  {
    id: "auto-start",
    name: "Auto Start",
    tagline: "CRM supplement (works alongside your stack)",
    description:
      "Deploy Frayze beside your existing CRM to capture every inbound lead, respond instantly, and keep reviews flowing without changing current workflows.",
    audience: "Single-location shops",
    bestFor: "Service centers and single rooftops",
    setupFee: "$1,250 setup",
    monthly: "$550/mo + usage",
    price: "$550/mo + usage",
    billing: "Month-to-month engagement",
    note: "Smart site + AI chatbot + unified inbox + review automation",
    valueProps: [
      "Smart automotive microsite and AI chatbot that books appointments",
      "Unified inbox for calls, SMS, DMs, and Google Business Messages",
      "Missed-call text-back with AI concierge replies",
      "Automated review requests and reporting dashboard"
    ],
    primaryCtaLabel: "Book rollout call",
    primaryCtaHref: "/book-consultation/",
    secondaryCtaLabel: "See service workflow",
    secondaryCtaHref: "/automotive/",
  },
  {
    id: "auto-grow",
    name: "Auto Grow",
    tagline: "Onboard to the Frayze CRM",
    description:
      "Layer in the Frayze CRM to automate nurture, service reminders, and post-visit follow-up so busy shops and small dealers stay in front of every opportunity.",
    audience: "Busy shops / small dealers",
    bestFor: "Busy shops and small independent dealers",
    setupFee: "$2,500 setup",
    monthly: "$1,250/mo + usage",
    price: "$1,250/mo + usage",
    billing: "Quarterly agreement",
    note: "Adds CRM automations, service reminders, and nurture flows",
    valueProps: [
      "Full pipeline automations with task and SLA tracking",
      "Service reminders, re-engagement, and post-visit follow-up",
      "Dealer-grade dashboards for advisor or rep accountability",
      "AI-written review responses and reputation monitoring"
    ],
    primaryCtaLabel: "Plan my upgrade",
    primaryCtaHref: "/book-consultation/",
    secondaryCtaLabel: "Compare packages",
    secondaryCtaHref: "/automotive/",
  },
  {
    id: "auto-dominate",
    name: "Auto Dominate",
    tagline: "Full-stack rollout",
    description:
      "Transform multi-rooftop operations with Frayze as the core platform: website, inventory, SEO, governance, and reporting all in one place.",
    audience: "Dealer groups / multi-rooftop",
    bestFor: "Multi-rooftop groups and franchise teams",
    setupFee: "$5,000 setup",
    monthly: "$2,250+/mo + usage",
    price: "$2,250+/mo + usage",
    billing: "Annual partnership",
    note: "Adds blazing-fast website, inventory feeds, SEO, vendor syncs, and governance",
    valueProps: [
      "BDC and showroom shared inbox with permissions",
      "Inventory-aware AI that books test drives and appraisals",
      "Regional reporting with revenue attribution and ROI",
      "Security, compliance, and advanced multi-location governance"
    ],
    primaryCtaLabel: "Schedule strategy call",
    primaryCtaHref: "/book-consultation/",
    secondaryCtaLabel: "See dealer workflow",
    secondaryCtaHref: "/automotive/dealers/",
  },
];

export const automotiveContentMap: Record<AutomotiveVariantKey, AutomotivePageContent> = {
  general: {
    key: "general",
    slug: "/automotive/",
    meta: {
      title: "AI-Powered Automotive Marketing System for Canadian Dealers | Frayze",
      description:
        "The complete automotive marketing system for Canadian dealers: speed-to-lead AI, used-car SEO, inventory, reviews and lead attribution in one managed system. Built by an operator who ran the floor.",
      canonical: "https://frayze.ca/automotive/",
      keywords:
        "automotive marketing system canada, auto dealer marketing canada, automotive lead generation, used car seo, automotive ai, dealership website, missed call text back, speed to lead, automotive marketing agency canada",
      ogTitle: "Frayze | The Operator-Built Automotive Marketing System for Canadian Dealers",
      ogDescription:
        "Speed-to-lead AI, used-car SEO, reviews and inventory in one managed system: built by someone who's actually run the dealership floor.",
      ogImage: "/frayze-og-images/main_link_image_optimized.jpg",
    },
    hero: {
      eyebrow: "Built by an operator, not a vendor",
      title: "The growth system for dealers who are done losing leads.",
      description:
        "Speed-to-lead, AI follow-up, reviews, inventory and used-car SEO in one system: built by someone who ran the lead engine across a six-rooftop group.",
      supporting: "For independent dealers, Tier-2 finance-first teams, multi-rooftop groups, and service centers.",
      audienceBullets: ["Speed-to-Lead AI", "Used-Car SEO", "No-DMS Inventory"],
      primaryCta: "See it in action",
      secondaryCta: "Book a Strategy Consult",
    },
    credibility: "Powering the new standard of automotive retail",
    problems: [],
    features: [],
    offers: baseOffers,
    serviceSchemaName: "Automotive Financial Operating System",
    subpages: [
      {
        title: "Automotive Dealers",
        description: "BDC-ready workflows for independent, used, franchise, and multi-rooftop groups.",
        href: "/automotive/dealers/",
      },
      {
        title: "Service Centers",
        description: "Repair, tire, quick-lube, and specialist bays with faster RO turnaround and automated booking.",
        href: "/automotive/",
      },
      {
        title: "Marine",
        description: "Seasonal demand automation for boat sales, service, storage, and winterization.",
        href: "/automotive/",
      },
      {
        title: "Power Sports",
        description: "AI follow-up + booking engines for ATVs, sleds, bikes, side-by-sides, and motorsports dealers.",
        href: "/automotive/",
      },
    ],
    proof: {
      statement: "See a real dealer build.",
      supporting: "The Motoring District: website, inventory engine, deal desk and CRM: one system for a three-in-one operation.",
      linkLabel: "Read the Motoring District case study",
      linkHref: "/case-studies/the-motoring-district-inventory-engine/",
    },
    faq: [],
    stickyCta: {
      heading: "Start Growing",
      secondary: "Book Consult"
    },
    demo: {
      url: BASE_DEMO_URL,
      heading: "See the Frayze dealer system in action",
      description: "Watch how speed-to-lead, AI follow-up, and inventory syndication work together."
    },
    experience: {
      heading: "Dealer-Specific Wins (Hard Numbers)",
      subheading: "This is production-tested automotive automation.",
      intro: "Real results from real rooftops.",
      bullets: [],
      stats: [
        {
          value: "6",
          label: "Rooftops Unified",
          description: "Under one system."
        },
        {
          value: "700+",
          label: "Vehicles Synced",
          description: "Live availability."
        },
        {
          value: "400%",
          label: "Lead Lift",
          description: "Verified credit leads."
        },
        {
          value: "$1.7M",
          label: "Annual Savings",
          description: "Reduced vendor bloat."
        },
        {
          value: "55%",
          label: "Lower Tech Cost",
          description: "Consolidated stack."
        },
        {
          value: "1.5M",
          label: "AI Conversations",
          description: "Handled automatically."
        },
        {
          value: "<10s",
          label: "Response Time",
          description: "Across all channels."
        }
      ]
    },
    systemsIntro: {
      eyebrow: "The Automotive Power Stack",
      title: "Get found. Get them approved. Close the sale.",
      description: "Local SEO that brings buyers in, paired with finance tools that turn shoppers into approved deals."
    },
    systems: [],
    caseStudy: {
      heading: "Case Study: The Northern Ontario Advantage",
      intro: "How a rural dealer outranked the big-city agencies.",
      problems: [
        "Overlooked by Toronto agencies",
        "Tough subprime credit mix",
        "Inconsistent follow-up across staff"
      ],
      solutions: [
        "Local, hands-on partnership",
        "Credit-first lead capture",
        "Operator-built follow-up that actually runs"
      ],
      outcomes: [
        "Owns its local market",
        "Higher customer retention",
        "Record F&I gross"
      ]
    },
    addons: {
      heading: "Strategic Add-ons",
      items: [
        {
          title: "Frayze Pulse AI™",
          description: "Your sales manager's second brain: surfaces the hottest prospects and auto-re-engages cooling leads, no deep-diving required."
        },
        {
          title: "Payments & Invoicing",
          description: "Collect deposits and send service invoices right over text and email."
        },
        {
          title: "Custom Dashboards",
          description: "Reporting built around your group's KPIs (Power BI / Looker)."
        },
        {
          title: "QR Codes & Calendars",
          description: "Lot-walk QR codes and team scheduling, done for you."
        }
      ]
    },
    closingNote: {
      heading: "Ready to sell more cars?",
      description: "Join the 2026 pilot program for Northern Ontario dealers and start booking more buyers.",
      primaryCtaLabel: "See the stack live",
      primaryCtaHref: "/book-consultation/",
    },
    comparison: {
      eyebrow: "Built by an operator, not a vendor",
      heading: "I've run DealerSocket, VinSolutions, Activix and Matador. Frayze is what I wish they'd been.",
      intro: "Frayze isn't a DMS: and it isn't trying to replace one. It's the speed-to-lead, AI and reputation layer that sits in front of whatever you already run, and works every lead until it buys. Built by someone who actually ran these tools across a six-rooftop group.",
      toolsRunLine: "Personally run in the wild: DealerSocket · VinSolutions · Activix · Matador · HubSpot · SalesPype · ZopDealer · AutoDealersDigital · V12",
      columns: [
        { name: "Your DMS / CRM", subtitle: "DealerSocket, VinSolutions, Activix" },
        { name: "Point tools", subtitle: "Matador, Podium, V12, ZopDealer" },
        { name: "Frayze", subtitle: "The lead + AI layer", highlight: true },
      ],
      rows: [
        { capability: "Speed-to-lead under 5 minutes, every channel", values: ["partial", "partial", true] },
        { capability: "24/7 AI voice + chat that actually books", values: [false, "partial", true] },
        { capability: "Missed-call text-back", values: [false, true, true] },
        { capability: "Dead-deal & database reactivation", values: ["partial", false, true] },
        { capability: "Review & reputation engine", values: [false, true, true] },
        { capability: "Inventory tool + syndication for a no-DMS lot", values: [false, false, true] },
        { capability: "Built and priced for independents", values: [false, "partial", true] },
        { capability: "One system: no vendor juggling", values: [false, false, true] },
        { capability: "Built by someone who's sold the cars", values: [false, false, true] },
      ],
      wedge: "Your DMS runs the back office: desking, F&I, accounting. Keep it. Frayze catches the lead before it walks to the dealer down the road, then works it until it shows up on your lot.",
    },
    sixPillars: {
      eyebrow: "The full system",
      heading: "Six pillars. One revenue engine.",
      intro: "Most dealers buy six tools from six vendors and spend their day stitching them together. Frayze runs all six as one connected system: turn on what you need, add the rest as you grow.",
      pillars: [
        { number: "01", title: "Inventory & Merchandising", tagline: "Get every unit online, fast and everywhere.", points: ["Inventory tool or DMS feed", "Syndication to AutoTrader, CarGurus, Kijiji & Marketplace", "Aged-unit, price-drop & needs-photos alerts", "Inventory-driven auto social posts"] },
        { number: "02", title: "Capture & Speed-to-Lead", tagline: "Answer every lead in seconds, on every channel.", points: ["Instant responders across web, SMS, DM & calls", "Missed-call text-back", "AI chat & voice that qualify and book", "Trade-in & financing intakes (credit bureau, AutoVerify, Carfax)"] },
        { number: "03", title: "Follow-up & Conversion", tagline: "Where the money actually is.", points: ["Speed-to-lead & long-term nurture sequences", "Dead-deal reactivation", "Equity & service-to-sales mining", "Appointment & show automation"] },
        { number: "04", title: "Reputation & Retention", tagline: "Turn every sale into the next one.", points: ["Automated review generation & monitoring", "Review-to-social loop", "Service reminders", "Repeat-purchase nurture"] },
        { number: "05", title: "Demand", tagline: "Fill the top of the funnel.", points: ["Local SEO & Google Business Profile", "Google & Meta ad management", "Pixels & analytics", "Landing pages & funnels"] },
        { number: "06", title: "Attribution", tagline: "Know what's actually working.", points: ["Ad-to-call & ad-to-sold tracking", "Source on every lead", "Live dealer dashboard", "Cost-per-sold-vehicle by channel"] },
      ],
    },
    aiShowcase: {
      eyebrow: "AI & automation",
      heading: "The AI layer the big platforms bolt on: built in from day one.",
      intro: "Not a chatbot widget. A connected AI layer that answers, qualifies, books and follows up across every channel, 24/7: so no lead waits until morning and no salesperson 'forgets' the third touch.",
      capabilities: [
        { title: "AI Voice Agent", description: "Answers inbound calls and follows up on leads 24/7, qualifies the buyer, and books the appointment straight into the calendar." },
        { title: "AI Web & SMS Chat", description: "Inventory-aware chat that answers real questions, captures the lead, and pushes for the test drive: day or night." },
        { title: "Missed-Call Text-Back", description: "Every missed call fires an instant text so the lead never bounces to the dealer down the street." },
        { title: "AI Vehicle Matching", description: "Matches buyers (including subprime) to the right unit against live inventory: no embarrassing rejections, no wrong cars shown." },
        { title: "Document AI", description: "Collects and chases the documents a deal needs, automatically, so finance isn't stuck waiting on the customer." },
        { title: "Dead-Deal Reactivation", description: "AI works your cold database and stalled quotes in the background and surfaces the ones ready to buy again." },
      ],
      proof: {
        heading: "Proven inside a six-rooftop dealer group: before Frayze was Frayze.",
        points: [
          "700+ cars matched daily against a rolling live inventory",
          "Voice agents built on ElevenLabs and Vapi, in 6 languages",
          "First-of-its-kind 2-way credit integration",
          "72 staff live across 6 rooftops in 10 days: on AI and automation",
        ],
        linkLabel: "Read the founder-built Need A Car case study",
        linkHref: "/case-studies/need-a-car-automotive-growth/",
      },
    },
    inventoryEngine: {
      eyebrow: "The independent's unfair advantage",
      heading: "No DMS? We build the inventory engine the big platforms won't.",
      intro: "DealerSocket-class systems are overkill and overpriced for a 30-car lot: so most independents run on spreadsheets and Marketplace. Frayze builds you a real inventory tool, syndication, and follow-up: the system a small dealer could never otherwise afford.",
      features: [
        { title: "Custom inventory tool", description: "A real back-end to manage stock: no DMS required, no per-seat enterprise contract." },
        { title: "VIN-decode intake", description: "Scan or type a VIN, auto-fill year/make/model/trim, optimize photos in the browser, publish." },
        { title: "Everywhere syndication", description: "One entry pushes the unit to AutoTrader, CarGurus, Kijiji Autos and Facebook Marketplace." },
        { title: "Inventory-driven social", description: "New units and price drops auto-post to your social: merchandising that runs itself." },
        { title: "SEO vehicle pages", description: "Every unit gets a fast, search-friendly detail page that ranks for 'used {make} {model} {city}'." },
        { title: "Built-in deal desk & lead magnets", description: "Payment calculators, trade-in & affordability tools, and an F&I deal desk: capture and close in one place." },
      ],
      proof: "Live today at The Motoring District (Greater Sudbury): a full inventory + deal-desk + lead-magnet system built for an independent with no DMS.",
      ctaLabel: "See the dealer system",
      ctaHref: "/automotive/dealers/",
    },
    websiteSeo: {
      eyebrow: "Websites & used-car SEO",
      heading: "We don't just run your leads: we build the site they land on.",
      intro: "The lead system is only as good as the site feeding it. Whether you own your site or you're locked into an OEM platform, Frayze builds the pages that rank, capture, and convert used-car buyers.",
      tracks: [
        {
          badge: "Independent dealers",
          title: "Your whole site, built to sell",
          tagline: "Fast, SEO-first, inventory baked in.",
          points: [
            "Modern dealer site built for speed and Google",
            "Inventory engine + ranking vehicle detail pages baked in",
            "Lead capture on every page (trade, finance, test drive)",
            "Owned and managed by us: no DIY, no vendor juggling",
          ],
        },
        {
          badge: "Franchise / OEM dealers",
          title: "We manage your used-car sites",
          tagline: "Keep your OEM site untouched. Win the used-car search anyway.",
          points: [
            "Independent 'Used Cars in {City}' authority sites that rank where your OEM site can't",
            "100% OEM-compliant and non-invasive: we never touch your franchise site",
            "Capture the used-car lead and feed it straight into your CRM",
            "Owned lead magnets (credit, trade, payment tools) on assets you control",
          ],
          footnote: "Your used inventory is where the margin and the search freedom live. We own that lane for you.",
        },
      ],
      proof: "Before Frayze, at a 6-rooftop Ontario auto group, we rebuilt the site in-house and ran it to 600–700% monthly traffic: syndicated to AutoTrader, CarGurus, Kijiji and Google Merchant: plus dedicated used-car authority sites that dominated local organic.",
    },
    tiers: {
      eyebrow: "What you can turn on",
      heading: "Three tiers. Turn on what you need, scale as you grow.",
      intro: "No rigid packages. Every dealer starts where it makes sense and adds the rest as they grow: these are the capabilities each tier turns on.",
      tiers: [
        {
          name: "Core",
          tagline: "Capture & respond",
          turnsOn: [
            "Dealer website + ranking vehicle pages",
            "Inventory tool or DMS feed",
            "Syndication to AutoTrader, CarGurus, Kijiji & Marketplace",
            "Instant lead responders on every channel",
            "Missed-call text-back",
            "Reviews & reputation engine",
            "Basic social posting",
            "Database reactivation",
          ],
        },
        {
          name: "Growth",
          tagline: "Automate & convert",
          carriesOver: "Everything in Core, plus:",
          highlight: true,
          turnsOn: [
            "Full lead-flow automation & CRM pipeline",
            "Long-term nurture + dead-deal reactivation",
            "Integration stack (credit bureau, AutoVerify, Carfax)",
            "AI chat that qualifies & books",
            "Reputation management",
            "Basic attribution & reporting",
          ],
        },
        {
          name: "Group / Authority",
          tagline: "Scale & attribute",
          carriesOver: "Everything in Growth, plus:",
          turnsOn: [
            "Google & Meta ad management",
            "Full ad-to-sold attribution",
            "AI voice agent, 24/7",
            "Multi-rooftop & shared inventory",
            "Dedicated account manager",
            "Custom integrations",
          ],
        },
      ],
      note: "Usage (calls, texts, AI) is billed at cost. The onboarding build is scoped to your store: pricing comes on a quick call once we know your rooftop(s).",
      ctaLabel: "Book a scoping call",
      ctaHref: AUTOMOTIVE_BOOKING_URL,
    },
  },
  serviceCenters: {
    key: "serviceCenters",
    slug: "/automotive/",
    meta: {
      title: "Service Center CRM & Booking Automation | Frayze",
      description: "Fill every bay and book more service automatically. Frayze handles bookings, reminders, and follow-up so advisors focus on cars.",
      canonical: "https://frayze.ca/automotive/",
      ogTitle: "Automotive Service CRM | Frayze",
      ogDescription: "Automate service bookings and follow-ups. Fill your bays without burning out your advisors.",
      ogImage: "/frayze-og-images/og-automotive.jpg",
      keywords: "automotive service scheduling software, auto repair shop software, garage management system",
    },
    hero: {
      title: "Fill every bay. Approve ROs faster. Keep customers coming back.",
      description: "Your shop doesn't need more calls. It needs faster booking, clear status updates, and follow-up that runs itself instead of leaning on busy advisors.",
      supporting: "Frayze closes the gaps that cost you repair orders and repeat customers.",
      audienceBullets: ["Repair shops", "Tire shops", "Quick-lube", "Specialists", "Multi-bay operations"],
      primaryCta: "See it in action",
      secondaryCta: "Book a 20-Min Service Consult",
    },
    experience: {
      heading: "What's really slowing down your service bays",
      subheading: "Frayze fixes the three things that cost shops the most: communication, coordination, and consistency.",
      intro: "Missed calls during the rush, advisors juggling phones and the counter, and no-show appointments add up to lost revenue fast.",
      bullets: [
        "Missed calls during rush",
        "Advisors juggling phones + counter",
        "No-show or delayed appointments",
        "Customers waiting on repair-status updates",
        "No automation for reminders or approvals",
        "Inconsistent upsells (alignments, fluids, tires)",
        "No consolidated inbox for service communication"
      ],
      stats: [
        {
          value: "35–50%",
          label: "Faster RO Turnaround",
          description: "Through automated communication."
        },
        {
          value: "2×",
          label: "More Bookings",
          description: "Completed bookings."
        },
        {
          value: "100%",
          label: "Accountability",
          description: "Follow-up accountability."
        },
        {
          value: "40%+",
          label: "More Bookings",
          description: "More completed bookings."
        },
        {
          value: "5-Star",
          label: "CSI Scores",
          description: "More 5-star CSI scores."
        }
      ]
    },
    systemsIntro: {
      eyebrow: "The 4 Service Systems",
      title: "Automate your service operations end to end.",
      description: "From first call to pickup, every step runs itself so you book more and chase less."
    },
    systems: [
      {
        id: "service-crm",
        title: "Service CRM Growth System",
        tagline: "One inbox for every booking. Phones, SMS, chat, and web forms in one place.",
        description: "Includes:",
        included: [
          {
            title: "Automated RO Follow-Up",
            description: "Missed-call text-back, Repair-status messages, Approval requests, Pickup notifications."
          },
          {
            title: "Service Workflow Automations",
            description: "Appointment → Reminder → Check-in, RO delay alerts, Upsell flows (alignment, tire rotation, fluid checks), Post-service CSI surveys."
          },
          {
            title: "Advisor Performance Dashboards",
            description: "Follow-up rate, Response time, RO close rate, Missed opportunity alerts."
          }
        ],
        outcome: "Your bays stay full, customers stay informed, and advisors stay focused on cars."
      },
      {
        id: "service-lead-gen",
        title: "Service Lead Gen System",
        tagline: "Google Ads built to fill your service schedule.",
        description: "Includes:",
        included: [
          {
            title: "Service-specific campaigns",
            description: "Tire change, oil change, brake service, AC repair, seasonal maintenance."
          },
          {
            title: "Local SEO",
            description: "Rank for 'auto repair near me', 'brake repair', 'oil change', etc."
          },
          {
            title: "Landing pages that convert",
            description: "Online booking optimized for mobile."
          },
          {
            title: "Automated follow-up",
            description: "From click → booking."
          }
        ],
        outcome: "Better traffic, lower cost per booking, and a steady flow of service appointments."
      },
      {
        id: "service-visibility",
        title: "Service Visibility System",
        tagline: "Show up first when drivers search for service nearby.",
        description: "Includes:",
        included: [
          {
            title: "Review Engine",
            description: "CSI surveys, structured review prompts, AI review responses."
          },
          {
            title: "GMB Optimization",
            description: "Update hours, seasonal offers, posts, services."
          },
          {
            title: "Speed-optimized service pages",
            description: "Fast loading pages for service menus."
          }
        ],
        outcome: "Your shop becomes the obvious choice in your area."
      },
      {
        id: "service-ai",
        title: "Service AI Intelligence System",
        tagline: "Let AI handle the phones so advisors can focus on the cars.",
        description: "Includes:",
        included: [
          {
            title: "AI Appointment Setter",
            description: "Books services, recommends time slots, sends confirmations."
          },
          {
            title: "AI Voice Agent",
            description: "Answers calls during rush or after hours."
          },
          {
            title: "AI Repair-Status Agent",
            description: "Handles 'Is my car ready?' queries."
          },
          {
            title: "AI Upsell Logic",
            description: "Suggests relevant RO add-ons based on vehicle type and season."
          }
        ],
        outcome: "Your advisors stop firefighting and start managing the bay."
      }
    ],
    recommendedRollout: {
      heading: "Workflows built for your type of shop",
      intro: "Ready-made flows for every kind of service business so you launch fast:",
      steps: [
        {
          title: "The Tire-Shop Flow",
          description: "Tire quote request → instant SMS → Fitment questions → AI answers → Inventory availability → automated → Booking → automated reminders."
        },
        {
          title: "The Repair-Shop Flow",
          description: "Drop-off → automated repair-status → Waiting on parts → delay message → Ready for pickup → instant alert."
        },
        {
          title: "The Quick-Lube Flow",
          description: "Drive-in queue → automated updates → Upsell flows → AI recommends services → Review prompt afterward."
        }
      ]
    },
    features: [
      {
        title: "Unified Service Inbox",
        description: "Route calls, chat, SMS, and web forms to the right advisor in one queue.",
        href: "/solutions/crm-automation/",
      },
      {
        title: "Automated RO Follow-Up",
        description: "Missed-call text-back, Repair-status messages, Approval requests, Pickup notifications.",
        href: "/solutions/crm-automation/",
      },
      {
        title: "Service Workflow Automations",
        description: "Appointment → Reminder → Check-in, RO delay alerts, Upsell flows.",
        href: "/solutions/lead-generation-system/",
      },
      {
        title: "Advisor Performance Dashboards",
        description: "Follow-up rate, Response time, RO close rate, Missed opportunity alerts.",
        href: "/solutions/ai-strategy-rollout/",
      },
      {
        title: "Review Engine",
        description: "CSI surveys, structured review prompts, AI review responses.",
        href: "/solutions/lead-generation-system/",
      },
    ],
    offers: [
      {
        ...baseOffers[0],
        description:
          "CRM supplement for service shops that need instant follow-up without ripping out systems.",
        valueProps: [
          "Shared inbox for phones, chat, and web forms",
          "AI books service appointments with reminders",
          "Service scripts, tags, and follow-up cadences baked in",
          "Review automation tied to advisor performance"
        ],
        primaryCtaLabel: "See service workflow",
        primaryCtaHref: "/automotive/",
        secondaryCtaLabel: "View Pricing",
        secondaryCtaHref: "/pricing/"
      },
      {
        ...baseOffers[1],
        description:
          "Onboard to the Frayze CRM: add nurture, reactivation, reviews, and manager escalations to keep bays full.",
        valueProps: [
          "Segmented nurture by vehicle type or season",
          "Service reminder flows",
          "Dashboards for service and parts",
          "AI-generated replies that keep tone on-brand"
        ],
        primaryCtaLabel: "Compare packages",
        primaryCtaHref: "/pricing/",
      },
    ],
    proof: {
      eyebrow: "Service Snapshot",
      statement: "Faster RO turnaround and fuller service bays.",
      supporting:
        "Automated communication reduced phone tag and kept customers informed.",
      linkLabel: "Read Case Study",
      linkHref: "/case-studies/need-a-car-automotive-growth/",
    },
    closingNote: {
      heading: "Ready to keep every bay full?",
      description: "Automate the busy work so your advisors can focus on the cars and the upsells.",
      primaryCtaLabel: "See the service system live",
      primaryCtaHref: "/book-consultation/",
      secondaryCtaLabel: "Book a Service Consult",
      secondaryCtaHref: AUTOMOTIVE_BOOKING_URL
    },
    problems: [],
    credibility: "Field-Proven Results",
    credibilityNotes: [],
    credibilityCard: undefined,
    reviewWidget: undefined,
    faq: [],
    stickyCta: {
      heading: "See the service system live",
      secondary: "Book a Service Consult"
    },
    demo: {
      url: BASE_DEMO_URL,
      heading: "See how Frayze keeps your bays full automatically.",
      description: "Watch the AI take a booking from first message to confirmed appointment."
    },
    serviceSchemaName: "Automotive Service Center Automation"
  },
  dealers: {
    key: "dealers",
    slug: "/automotive/dealers/",
    meta: {
      // Title targets the proven GSC cluster this page already surfaces for but ranks p35-55 on:
      // "canadian dealer crm software" (p43), "dealer crm in canada" (p35), "dealer crm software in
      // canada" (p49). The old title carried "Automotive CRM" but never the word buyers actually
      // type: software. "Fully Managed" keeps the managed-vs-self-serve moat (2026-06-18 decision).
      title: "Canadian Dealer CRM Software, Fully Managed | Frayze",
      description:
        "Canadian dealer CRM software, fully managed for independent dealers. We answer every lead in minutes, book test drives, and run it all for you. No DMS swap needed.",
      canonical: "https://frayze.ca/automotive/dealers/",
      ogTitle: "Canadian Dealer CRM Software, Fully Managed | Frayze",
      ogDescription:
        "AI concierge, shared inbox, and test-drive booking: managed by Frayze so you win the buyer before another lot replies.",
      keywords: "canadian dealer crm software, dealer crm in canada, dealer crm software in canada, canadian vehicle crm software, dealership software canada, automotive crm, automotive crm canada, independent dealer crm canada, managed automotive crm, dealership crm, automotive bdc service, dealer lead attribution",
    },
    hero: {
      // Eyebrow carries the keyword as visible on-page text so the H1 can stay a conversion line
      // rather than a keyword string.
      eyebrow: "Canadian Dealer CRM Software",
      title: "Win the buyer before they shop another lot.",
      description:
        "Frayze is the managed automotive CRM for independent Canadian dealers: we answer missed calls, Marketplace chats, and DMs in seconds, share inventory, and book test drives before your competition even replies. You sell. We run the system.",
      supporting: "BDC managers • Independent dealers • Small groups",
      audienceBullets: ["BDC managers", "Independent dealers", "Small groups"],
      primaryCta: "See it in action",
      secondaryCta: "Book a 20-min consult",
    },
    credibility: "Why dealers choose Frayze",
    credibilityNotes: [
      "<10s response, every time. AI concierge replies on phones, chat, SMS, and Marketplace in seconds.",
      "One pipeline, zero chaos. Phones, web forms, and social roll into a single queue with assignments and next steps.",
      "Accountability by design. Speed-to-lead, appointment rate, and show rate by agent, campaign, and rooftop.",
    ],
    credibilityCard: {
      title: "99.7% of phone calls get answered.",
      stats: "AI routing + concierge coverage",
      description: "Missed-call text-back, live transfer, and queue visibility keep buyers engaged.",
    },
    scaleProof: {
      heading: "Usage billed at cost",
      description: "Calls, texts, and AI usage are billed pass-through. Pay only for the conversations you win.",
    },
    problems: [
      {
        problem: "BDC chasing voicemails",
        solution: "AI books the appointment",
        description:
          "AI offers time slots, inventory details, and finance prompts right in the chat so buyers never wait.",
      },
      {
        problem: "Leads scattered across tools",
        solution: "One sales pipeline",
        description:
          "Marketplace, web forms, and calls feed one queue with tasks, scripts, and SLAs so no lead goes dark.",
      },
      {
        problem: "Reviews drop when sales spike",
        solution: "Always-on review engine",
        description:
          "Branded review requests fire after every delivery and AI replies in your tone, with zero extra work for your team.",
      },
    ],
    features: [
      {
        title: "Unified BDC inbox",
        description: "Route calls, chat, SMS, and Marketplace to the right salesperson, lender, or rooftop.",
        href: "/solutions/crm-automation/",
      },
      {
        title: "Instant text-back with inventory data",
        description: "AI shares stock numbers, trim, incentives, and payments in seconds.",
        href: "/solutions/crm-automation/",
      },
      {
        title: "Test-drive & appraisal booking",
        description: "Offer real-time slots with automated reminders and no-show nudges.",
        href: "/onboarding/",
      },
      {
        title: "BDC performance dashboards",
        description: "Track speed-to-lead, appointment, and show rate by agent, campaign, and rooftop.",
        href: "/solutions/ai-strategy-rollout/",
      },
      {
        title: "Automated review campaigns",
        description: "Review campaigns and AI responses tied to salesperson and rooftop performance.",
        href: "/solutions/lead-generation-system/",
      },
    ],
    offers: [
      {
        ...baseOffers[0],
        description:
          "CRM supplement for single rooftops or service-heavy dealers that need instant follow-up without ripping out systems.",
        valueProps: [
          "Shared inbox for phones, chat, Marketplace, and web forms",
          "Inventory-aware AI books test rides with payment prompts",
          "BDC scripts, tags, and follow-up cadences baked in",
          "Review automation tied to salesperson performance"
        ],
        primaryCtaLabel: "See dealer workflow",
        primaryCtaHref: "/automotive/dealers/",
        secondaryCtaLabel: "View Pricing",
        secondaryCtaHref: "/pricing/"
      },
      {
        ...baseOffers[1],
        description:
          "Onboard to the Frayze CRM: add nurture, reactivation, reviews, and manager escalations to keep riders engaged all season.",
        valueProps: [
          "Segmented nurture by vehicle type, season, or lender",
          "Finance and protection follow-up flows",
          "Dashboards for sales, service, and accessories",
          "AI-generated replies that keep tone on-brand"
        ],
        primaryCtaLabel: "Compare packages",
        primaryCtaHref: "/pricing/",
      },
      {
        ...baseOffers[2],
        description:
          "Full stack for multi-rooftop groups: website, inventory, SEO, governance, and executive dashboards.",
        valueProps: [
          "Multi-store routing, permissions, and compliance logging",
          "Marketing automations for events, demo days, and renewals",
          "Real-time KPIs for sales, service, and parts",
          "Executive scorecards with ROI attribution"
        ],
        primaryCtaLabel: "Schedule strategy call",
        primaryCtaHref: AUTOMOTIVE_BOOKING_URL,
      },
    ],
    proof: {
      eyebrow: "Dealer Snapshot",
      statement: "One system for a three-in-one dealership.",
      supporting:
        "The Motoring District runs sales, service and detailing on one Frayze build: website, inventory engine, deal desk and CRM, no double-entry.",
      linkLabel: "Read the Motoring District case study",
      linkHref: "/case-studies/the-motoring-district-inventory-engine/",
    },
    caseStudy: {
      heading: "Proof, not promises",
      intro: "Before Frayze, our founder built the lead engine for a six-rooftop independent group.",
      context: "700+ vehicles in stock, four language teams, and 180,000+ contacts managed by AI.",
      problems: [
        "Multiple rooftops with siloed CRMs and no shared queue",
        "Missed calls and voicemails with no accountability",
        "Marketing spend spread across vendors without attribution",
        "Review volume stalled when sales spiked"
      ],
      solutions: [
        "AI concierge covering phones, Marketplace, SMS, and web in under 10 seconds",
        "Shared pipeline with routing by rooftop, lender, and salesperson",
        "Executive dashboards with ROI and show-rate reporting",
        "Automated review engine responding in brand tone"
      ],
      outcomes: [
        "400% lead lift (same ad spend)",
        "52% lower tech costs",
        "$1.7M annual marketing savings",
        "1.5M AI-powered messages delivered in 2024",
        "Consistent governance and compliance logging across every rooftop"
      ]
    },
    recommendedRollout: {
      heading: "How rollout works",
      intro: "A step-by-step guide to implementing Frayze in your dealership.",
      steps: [
        {
          title: "Map calls, chats, and DMs",
          description: "Audit every inbound channel and configure routing by rooftop, lender, and salesperson.",
        },
        {
          title: "Launch AI follow-up",
          description: "Turn on missed-call text-back, Marketplace responders, and review flows within days.",
        },
        {
          title: "Train the BDC",
          description: "Equip advisors with scripts, dashboards, and coaching prompts for accountability.",
        },
      ],
    },
    resourcesLinks: [
      {
        label: "Read the Guide",
        description: "2026 Automotive Dealership AI CRM Playbook",
        href: "/blog/2026-automotive-dealership-ai-crm-playbook/"
      }
      ,{
        label: "GTA dealer groups",
        description: "The premium tier for multi-rooftop GTA groups",
        href: "/automotive/gta-dealers/"
      }
    ],
    faq: [
      {
        question: "Facebook Marketplace + website chat?",
        answer:
          "Yes. Marketplace, website, and third-party leads flow into one queue with instant assignments and source tracking.",
      },
      {
        question: "Works with our phones?",
        answer:
          "Keep your numbers. We wrap text-back, routing, and call recording around your existing provider.",
      },
      {
        question: "Push to our CRM/DMS?",
        answer:
          "Yes. Two-way sync keeps notes, tasks, and appointments aligned with your CRM/DMS and compliance logging.",
      },
    ],
    stickyCta: {
      heading: "See the dealer system live",
      secondary: "Book a 20-min consult",
    },
    demo: {
      url: BASE_DEMO_URL,
      heading: "See how missed calls, Marketplace chats, and DMs convert to booked test drives in under a minute.",
      description: "Walk the BDC workflow. From inbound lead to scheduled appointment and leadership reporting.",
    },
    comparison: {
      eyebrow: "Built by an operator, not a vendor",
      heading: "You've been pitched DealerSocket, VinSolutions and Activix. An operator who ran them built this instead.",
      intro: "Frayze isn't a DMS: and it isn't trying to replace one. It's the speed-to-lead, AI and reputation layer that sits in front of whatever you already run, and works every lead until it buys. Built by someone who actually ran these tools across a six-rooftop group.",
      toolsRunLine: "Personally run in the wild: DealerSocket · VinSolutions · Activix · Matador · HubSpot · SalesPype · ZopDealer · AutoDealersDigital · V12",
      columns: [
        { name: "Your DMS / CRM", subtitle: "DealerSocket, VinSolutions, Activix" },
        { name: "Point tools", subtitle: "Matador, Podium, V12, ZopDealer" },
        { name: "Frayze", subtitle: "The lead + AI layer", highlight: true },
      ],
      rows: [
        { capability: "Speed-to-lead under 5 minutes, every channel", values: ["partial", "partial", true] },
        { capability: "24/7 AI voice + chat that actually books", values: [false, "partial", true] },
        { capability: "Missed-call text-back", values: [false, true, true] },
        { capability: "Dead-deal & database reactivation", values: ["partial", false, true] },
        { capability: "Review & reputation engine", values: [false, true, true] },
        { capability: "Inventory tool + syndication for a no-DMS lot", values: [false, false, true] },
        { capability: "Built and priced for independents", values: [false, "partial", true] },
        { capability: "One system: no vendor juggling", values: [false, false, true] },
        { capability: "Built by someone who's sold the cars", values: [false, false, true] },
      ],
      wedge: "Your DMS runs the back office: desking, F&I, accounting. Keep it. Frayze catches the lead before it walks to the dealer down the road, then works it until it shows up on your lot.",
    },
    sixPillars: {
      eyebrow: "The full system",
      heading: "Six pillars. One revenue engine.",
      intro: "Independent dealers don't need six vendors and six logins. Frayze runs all six pillars as one system: start with what moves metal this month, add the rest as you grow.",
      pillars: [
        { number: "01", title: "Inventory & Merchandising", tagline: "Get every unit online, fast and everywhere.", points: ["Inventory tool or DMS feed", "Syndication to AutoTrader, CarGurus, Kijiji & Marketplace", "Aged-unit, price-drop & needs-photos alerts", "Inventory-driven auto social posts"] },
        { number: "02", title: "Capture & Speed-to-Lead", tagline: "Answer every lead in seconds, on every channel.", points: ["Instant responders across web, SMS, DM & calls", "Missed-call text-back", "AI chat & voice that qualify and book", "Trade-in & financing intakes (credit bureau, AutoVerify, Carfax)"] },
        { number: "03", title: "Follow-up & Conversion", tagline: "Where the money actually is.", points: ["Speed-to-lead & long-term nurture sequences", "Dead-deal reactivation", "Equity & service-to-sales mining", "Appointment & show automation"] },
        { number: "04", title: "Reputation & Retention", tagline: "Turn every sale into the next one.", points: ["Automated review generation & monitoring", "Review-to-social loop", "Service reminders", "Repeat-purchase nurture"] },
        { number: "05", title: "Demand", tagline: "Fill the top of the funnel.", points: ["Local SEO & Google Business Profile", "Google & Meta ad management", "Pixels & analytics", "Landing pages & funnels"] },
        { number: "06", title: "Attribution", tagline: "Know what's actually working.", points: ["Ad-to-call & ad-to-sold tracking", "Source on every lead", "Live dealer dashboard", "Cost-per-sold-vehicle by channel"] },
      ],
    },
    aiShowcase: {
      eyebrow: "AI & automation",
      heading: "The big platforms charge enterprise money for an AI bolt-on. We build it in.",
      intro: "A connected AI layer that answers, qualifies, books and follows up across every channel, 24/7: so a 30-car lot responds like a six-rooftop group, and no lead waits until morning.",
      capabilities: [
        { title: "AI Voice Agent", description: "Answers inbound calls and follows up on leads 24/7, qualifies the buyer, and books the appointment straight into the calendar." },
        { title: "AI Web & SMS Chat", description: "Inventory-aware chat that answers real questions, captures the lead, and pushes for the test drive: day or night." },
        { title: "Missed-Call Text-Back", description: "Every missed call fires an instant text so the lead never bounces to the dealer down the street." },
        { title: "AI Vehicle Matching", description: "Matches buyers (including subprime) to the right unit against live inventory: no embarrassing rejections, no wrong cars shown." },
        { title: "Document AI", description: "Collects and chases the documents a deal needs, automatically, so finance isn't stuck waiting on the customer." },
        { title: "Dead-Deal Reactivation", description: "AI works your cold database and stalled quotes in the background and surfaces the ones ready to buy again." },
      ],
      proof: {
        heading: "Proven inside a six-rooftop dealer group: before Frayze was Frayze.",
        points: [
          "700+ cars matched daily against a rolling live inventory",
          "Voice agents built on ElevenLabs and Vapi, in 6 languages",
          "First-of-its-kind 2-way credit integration",
          "72 staff live across 6 rooftops in 10 days: on AI and automation",
        ],
        linkLabel: "Read the founder-built Need A Car case study",
        linkHref: "/case-studies/need-a-car-automotive-growth/",
      },
    },
    inventoryEngine: {
      eyebrow: "The independent's unfair advantage",
      heading: "No DMS? That's our advantage, not your problem.",
      intro: "DealerSocket-class systems are overkill and overpriced for a 30-car lot: so most independents run on spreadsheets and Marketplace. Frayze builds you a real inventory tool, syndication, and follow-up: the system a small dealer could never otherwise afford.",
      features: [
        { title: "Custom inventory tool", description: "A real back-end to manage stock: no DMS required, no per-seat enterprise contract." },
        { title: "VIN-decode intake", description: "Scan or type a VIN, auto-fill year/make/model/trim, optimize photos in the browser, publish." },
        { title: "Everywhere syndication", description: "One entry pushes the unit to AutoTrader, CarGurus, Kijiji Autos and Facebook Marketplace." },
        { title: "Inventory-driven social", description: "New units and price drops auto-post to your social: merchandising that runs itself." },
        { title: "SEO vehicle pages", description: "Every unit gets a fast, search-friendly detail page that ranks for 'used {make} {model} {city}'." },
        { title: "Built-in deal desk & lead magnets", description: "Payment calculators, trade-in & affordability tools, and an F&I deal desk: capture and close in one place." },
      ],
      proof: "Live today at The Motoring District (Greater Sudbury): a full inventory + deal-desk + lead-magnet system built for an independent with no DMS.",
      ctaLabel: "Book a dealer walkthrough",
      ctaHref: AUTOMOTIVE_BOOKING_URL,
    },
    websiteSeo: {
      eyebrow: "Websites & used-car SEO",
      heading: "We don't just run your leads: we build the site they land on.",
      intro: "The lead system is only as good as the site feeding it. Whether you own your site or you're locked into an OEM platform, Frayze builds the pages that rank, capture, and convert used-car buyers.",
      tracks: [
        {
          badge: "Independent dealers",
          title: "Your whole site, built to sell",
          tagline: "Fast, SEO-first, inventory baked in.",
          points: [
            "Modern dealer site built for speed and Google",
            "Inventory engine + ranking vehicle detail pages baked in",
            "Lead capture on every page (trade, finance, test drive)",
            "Owned and managed by us: no DIY, no vendor juggling",
          ],
        },
        {
          badge: "Franchise / OEM dealers",
          title: "We manage your used-car sites",
          tagline: "Keep your OEM site untouched. Win the used-car search anyway.",
          points: [
            "Independent 'Used Cars in {City}' authority sites that rank where your OEM site can't",
            "100% OEM-compliant and non-invasive: we never touch your franchise site",
            "Capture the used-car lead and feed it straight into your CRM",
            "Owned lead magnets (credit, trade, payment tools) on assets you control",
          ],
          footnote: "Your used inventory is where the margin and the search freedom live. We own that lane for you.",
        },
      ],
      proof: "Before Frayze, at a 6-rooftop Ontario auto group, we rebuilt the site in-house and ran it to 600–700% monthly traffic: syndicated to AutoTrader, CarGurus, Kijiji and Google Merchant: plus dedicated used-car authority sites that dominated local organic.",
    },
    tiers: {
      eyebrow: "What you can turn on",
      heading: "Three tiers. Turn on what you need, scale as you grow.",
      intro: "No rigid packages. Every dealer starts where it makes sense and adds the rest as they grow: these are the capabilities each tier turns on.",
      tiers: [
        {
          name: "Core",
          tagline: "Capture & respond",
          turnsOn: [
            "Dealer website + ranking vehicle pages",
            "Inventory tool or DMS feed",
            "Syndication to AutoTrader, CarGurus, Kijiji & Marketplace",
            "Instant lead responders on every channel",
            "Missed-call text-back",
            "Reviews & reputation engine",
            "Basic social posting",
            "Database reactivation",
          ],
        },
        {
          name: "Growth",
          tagline: "Automate & convert",
          carriesOver: "Everything in Core, plus:",
          highlight: true,
          turnsOn: [
            "Full lead-flow automation & CRM pipeline",
            "Long-term nurture + dead-deal reactivation",
            "Integration stack (credit bureau, AutoVerify, Carfax)",
            "AI chat that qualifies & books",
            "Reputation management",
            "Basic attribution & reporting",
          ],
        },
        {
          name: "Group / Authority",
          tagline: "Scale & attribute",
          carriesOver: "Everything in Growth, plus:",
          turnsOn: [
            "Google & Meta ad management",
            "Full ad-to-sold attribution",
            "AI voice agent, 24/7",
            "Multi-rooftop & shared inventory",
            "Dedicated account manager",
            "Custom integrations",
          ],
        },
      ],
      note: "Usage (calls, texts, AI) is billed at cost. The onboarding build is scoped to your store: pricing comes on a quick call once we know your rooftop(s).",
      ctaLabel: "Book a scoping call",
      ctaHref: AUTOMOTIVE_BOOKING_URL,
    },
    serviceSchemaName: "Automotive dealer communication and booking automation",
  },

  gtaDealers: {
    key: "gtaDealers",
    slug: "/automotive/gta-dealers/",
    meta: {
      title: "Managed CRM for GTA Car Dealers | Built by a 6-Rooftop Operator | Frayze",
      description:
        "Managed automotive CRM for Greater Toronto Area dealers. Built by the operator who ran the lead engine across a 6-rooftop GTA dealer group. We answer every lead in minutes.",
      canonical: "https://frayze.ca/automotive/gta-dealers/",
      ogTitle: "Managed CRM for GTA Car Dealers | Frayze",
      ogDescription:
        "Built by the operator who ran the lead engine across a 6-rooftop GTA dealer group. We answer every lead in minutes so you win the buyer before the lot down the road.",
      keywords: "gta dealer crm, toronto car dealer crm, managed automotive crm toronto, gta dealership lead system, dealer bdc toronto, automotive crm gta, car dealer speed to lead toronto, greater toronto area dealer marketing",
    },
    hero: {
      eyebrow: "GTA Dealers · Managed Automotive CRM",
      title: "Win the buyer before the lot down the road does.",
      description:
        "In the GTA, the buyer has ten lots within a twenty-minute drive. The first credible answer wins. Frayze answers missed calls, Marketplace chats, and DMs in seconds, books the test drive, and runs the whole system for you. Built by an operator who ran the lead engine across a 6-rooftop GTA dealer group.",
      supporting: "BDC managers • Independent dealers • Small groups",
      audienceBullets: ["BDC managers", "Independent dealers", "Small groups"],
      primaryCta: "See it in action",
      secondaryCta: "Book a 20-min consult",
    },
    credibility: "Why GTA dealers choose Frayze",
    credibilityNotes: [
      "Built by a GTA dealer operator. Before Frayze, our founder ran the lead engine across a 6-rooftop Greater Toronto Area dealer group. This is the system that work produced.",
      "<10s response, every time. AI concierge replies on phones, chat, SMS, and Marketplace in seconds.",
      "One pipeline, zero chaos. Phones, web forms, and social roll into a single queue with assignments and next steps.",
      "Accountability by design. Speed-to-lead, appointment rate, and show rate by agent, campaign, and rooftop.",
    ],
    credibilityCard: {
      title: "99.7% of phone calls get answered.",
      stats: "AI routing + concierge coverage",
      description: "Missed-call text-back, live transfer, and queue visibility keep buyers engaged.",
    },
    scaleProof: {
      heading: "Usage billed at cost",
      description: "Calls, texts, and AI usage are billed pass-through. Pay only for the conversations you win.",
    },
    problems: [
      {
        problem: "BDC chasing voicemails",
        solution: "AI books the appointment",
        description:
          "AI offers time slots, inventory details, and finance prompts right in the chat so buyers never wait.",
      },
      {
        problem: "Leads scattered across tools",
        solution: "One sales pipeline",
        description:
          "Marketplace, web forms, and calls feed one queue with tasks, scripts, and SLAs so no lead goes dark.",
      },
      {
        problem: "Reviews drop when sales spike",
        solution: "Always-on review engine",
        description:
          "Branded review requests fire after every delivery and AI replies in your tone, with zero extra work for your team.",
      },
    ],
    features: [
      {
        title: "Unified BDC inbox",
        description: "Route calls, chat, SMS, and Marketplace to the right salesperson, lender, or rooftop.",
        href: "/solutions/crm-automation/",
      },
      {
        title: "Instant text-back with inventory data",
        description: "AI shares stock numbers, trim, incentives, and payments in seconds.",
        href: "/solutions/crm-automation/",
      },
      {
        title: "Test-drive & appraisal booking",
        description: "Offer real-time slots with automated reminders and no-show nudges.",
        href: "/onboarding/",
      },
      {
        title: "BDC performance dashboards",
        description: "Track speed-to-lead, appointment, and show rate by agent, campaign, and rooftop.",
        href: "/solutions/ai-strategy-rollout/",
      },
      {
        title: "Automated review campaigns",
        description: "Review campaigns and AI responses tied to salesperson and rooftop performance.",
        href: "/solutions/lead-generation-system/",
      },
    ],
    offers: [
      {
        ...baseOffers[0],
        description:
          "CRM supplement for single rooftops or service-heavy dealers that need instant follow-up without ripping out systems.",
        valueProps: [
          "Shared inbox for phones, chat, Marketplace, and web forms",
          "Inventory-aware AI books test rides with payment prompts",
          "BDC scripts, tags, and follow-up cadences baked in",
          "Review automation tied to salesperson performance"
        ],
        primaryCtaLabel: "See dealer workflow",
        primaryCtaHref: "/automotive/dealers/",
        secondaryCtaLabel: "View Pricing",
        secondaryCtaHref: "/pricing/"
      },
      {
        ...baseOffers[1],
        description:
          "Onboard to the Frayze CRM: add nurture, reactivation, reviews, and manager escalations to keep riders engaged all season.",
        valueProps: [
          "Segmented nurture by vehicle type, season, or lender",
          "Finance and protection follow-up flows",
          "Dashboards for sales, service, and accessories",
          "AI-generated replies that keep tone on-brand"
        ],
        primaryCtaLabel: "Compare packages",
        primaryCtaHref: "/pricing/",
      },
      {
        ...baseOffers[2],
        description:
          "Full stack for multi-rooftop groups: website, inventory, SEO, governance, and executive dashboards.",
        valueProps: [
          "Multi-store routing, permissions, and compliance logging",
          "Marketing automations for events, demo days, and renewals",
          "Real-time KPIs for sales, service, and parts",
          "Executive scorecards with ROI attribution"
        ],
        primaryCtaLabel: "Schedule strategy call",
        primaryCtaHref: AUTOMOTIVE_BOOKING_URL,
      },
    ],
    proof: {
      eyebrow: "Founder Track Record",
      statement: "400% inbound lead growth across a 6-rooftop group.",
      supporting:
        "Before Frayze, our founder built and ran the lead engine at Need A Car / Toronto Auto Group: the systems this program is built on.",
      linkLabel: "Read the Need A Car story",
      linkHref: "/case-studies/need-a-car-automotive-growth/",
    },
    caseStudy: {
      heading: "Proof, not promises",
      intro: "Before Frayze, our founder built the lead engine for a six-rooftop independent group.",
      context: "700+ vehicles in stock, four language teams, and 180,000+ contacts managed by AI.",
      problems: [
        "Multiple rooftops with siloed CRMs and no shared queue",
        "Missed calls and voicemails with no accountability",
        "Marketing spend spread across vendors without attribution",
        "Review volume stalled when sales spiked"
      ],
      solutions: [
        "AI concierge covering phones, Marketplace, SMS, and web in under 10 seconds",
        "Shared pipeline with routing by rooftop, lender, and salesperson",
        "Executive dashboards with ROI and show-rate reporting",
        "Automated review engine responding in brand tone"
      ],
      outcomes: [
        "400% lead lift (same ad spend)",
        "52% lower tech costs",
        "$1.7M annual marketing savings",
        "1.5M AI-powered messages delivered in 2024",
        "Consistent governance and compliance logging across every rooftop"
      ]
    },
    recommendedRollout: {
      heading: "How rollout works",
      intro: "A step-by-step guide to implementing Frayze in your dealership.",
      steps: [
        {
          title: "Map calls, chats, and DMs",
          description: "Audit every inbound channel and configure routing by rooftop, lender, and salesperson.",
        },
        {
          title: "Launch AI follow-up",
          description: "Turn on missed-call text-back, Marketplace responders, and review flows within days.",
        },
        {
          title: "Train the BDC",
          description: "Equip advisors with scripts, dashboards, and coaching prompts for accountability.",
        },
      ],
    },
    resourcesLinks: [
      {
        label: "Read the Guide",
        description: "2026 Automotive Dealership AI CRM Playbook",
        href: "/blog/2026-automotive-dealership-ai-crm-playbook/"
      }
    ],
    faq: [
      {
        question: "Facebook Marketplace + website chat?",
        answer:
          "Yes. Marketplace, website, and third-party leads flow into one queue with instant assignments and source tracking.",
      },
      {
        question: "Works with our phones?",
        answer:
          "Keep your numbers. We wrap text-back, routing, and call recording around your existing provider.",
      },
      {
        question: "Push to our CRM/DMS?",
        answer:
          "Yes. Two-way sync keeps notes, tasks, and appointments aligned with your CRM/DMS and compliance logging.",
      },
    ],
    stickyCta: {
      heading: "See the dealer system live",
      secondary: "Book a 20-min consult",
    },
    demo: {
      url: BASE_DEMO_URL,
      heading: "See how missed calls, Marketplace chats, and DMs convert to booked test drives in under a minute.",
      description: "Walk the BDC workflow. From inbound lead to scheduled appointment and leadership reporting.",
    },
    comparison: {
      eyebrow: "Built by an operator, not a vendor",
      heading: "You've been pitched DealerSocket, VinSolutions and Activix. An operator who ran them built this instead.",
      intro: "Frayze isn't a DMS: and it isn't trying to replace one. It's the speed-to-lead, AI and reputation layer that sits in front of whatever you already run, and works every lead until it buys. Built by someone who actually ran these tools across a six-rooftop group.",
      toolsRunLine: "Personally run in the wild: DealerSocket · VinSolutions · Activix · Matador · HubSpot · SalesPype · ZopDealer · AutoDealersDigital · V12",
      columns: [
        { name: "Your DMS / CRM", subtitle: "DealerSocket, VinSolutions, Activix" },
        { name: "Point tools", subtitle: "Matador, Podium, V12, ZopDealer" },
        { name: "Frayze", subtitle: "The lead + AI layer", highlight: true },
      ],
      rows: [
        { capability: "Speed-to-lead under 5 minutes, every channel", values: ["partial", "partial", true] },
        { capability: "24/7 AI voice + chat that actually books", values: [false, "partial", true] },
        { capability: "Missed-call text-back", values: [false, true, true] },
        { capability: "Dead-deal & database reactivation", values: ["partial", false, true] },
        { capability: "Review & reputation engine", values: [false, true, true] },
        { capability: "Inventory tool + syndication for a no-DMS lot", values: [false, false, true] },
        { capability: "Built and priced for independents", values: [false, "partial", true] },
        { capability: "One system: no vendor juggling", values: [false, false, true] },
        { capability: "Built by someone who's sold the cars", values: [false, false, true] },
      ],
      wedge: "Your DMS runs the back office: desking, F&I, accounting. Keep it. Frayze catches the lead before it walks to the dealer down the road, then works it until it shows up on your lot.",
    },
    sixPillars: {
      eyebrow: "The full system",
      heading: "Six pillars. One revenue engine.",
      intro: "Independent dealers don't need six vendors and six logins. Frayze runs all six pillars as one system: start with what moves metal this month, add the rest as you grow.",
      pillars: [
        { number: "01", title: "Inventory & Merchandising", tagline: "Get every unit online, fast and everywhere.", points: ["Inventory tool or DMS feed", "Syndication to AutoTrader, CarGurus, Kijiji & Marketplace", "Aged-unit, price-drop & needs-photos alerts", "Inventory-driven auto social posts"] },
        { number: "02", title: "Capture & Speed-to-Lead", tagline: "Answer every lead in seconds, on every channel.", points: ["Instant responders across web, SMS, DM & calls", "Missed-call text-back", "AI chat & voice that qualify and book", "Trade-in & financing intakes (AutoCorp, AutoVerify, Carfax)"] },
        { number: "03", title: "Follow-up & Conversion", tagline: "Where the money actually is.", points: ["Speed-to-lead & long-term nurture sequences", "Dead-deal reactivation", "Equity & service-to-sales mining", "Appointment & show automation"] },
        { number: "04", title: "Reputation & Retention", tagline: "Turn every sale into the next one.", points: ["Automated review generation & monitoring", "Review-to-social loop", "Service reminders", "Repeat-purchase nurture"] },
        { number: "05", title: "Demand", tagline: "Fill the top of the funnel.", points: ["Local SEO & Google Business Profile", "Google & Meta ad management", "Pixels & analytics", "Landing pages & funnels"] },
        { number: "06", title: "Attribution", tagline: "Know what's actually working.", points: ["Ad-to-call & ad-to-sold tracking", "Source on every lead", "Live dealer dashboard", "Cost-per-sold-vehicle by channel"] },
      ],
    },
    aiShowcase: {
      eyebrow: "AI & automation",
      heading: "The big platforms charge enterprise money for an AI bolt-on. We build it in.",
      intro: "A connected AI layer that answers, qualifies, books and follows up across every channel, 24/7: so a 30-car lot responds like a six-rooftop group, and no lead waits until morning.",
      capabilities: [
        { title: "AI Voice Agent", description: "Answers inbound calls and follows up on leads 24/7, qualifies the buyer, and books the appointment straight into the calendar." },
        { title: "AI Web & SMS Chat", description: "Inventory-aware chat that answers real questions, captures the lead, and pushes for the test drive: day or night." },
        { title: "Missed-Call Text-Back", description: "Every missed call fires an instant text so the lead never bounces to the dealer down the street." },
        { title: "AI Vehicle Matching", description: "Matches buyers (including subprime) to the right unit against live inventory: no embarrassing rejections, no wrong cars shown." },
        { title: "Document AI", description: "Collects and chases the documents a deal needs, automatically, so finance isn't stuck waiting on the customer." },
        { title: "Dead-Deal Reactivation", description: "AI works your cold database and stalled quotes in the background and surfaces the ones ready to buy again." },
      ],
      proof: {
        heading: "Proven inside a six-rooftop dealer group: before Frayze was Frayze.",
        points: [
          "700+ cars matched daily against a rolling live inventory",
          "Voice agents built on ElevenLabs and Vapi, in 6 languages",
          "First-of-its-kind 2-way AutoCorp credit integration",
          "72 staff live across 6 rooftops in 10 days: on AI and automation",
        ],
        linkLabel: "Read the founder-built Need A Car case study",
        linkHref: "/case-studies/need-a-car-automotive-growth/",
      },
    },
    inventoryEngine: {
      eyebrow: "The independent's unfair advantage",
      heading: "No DMS? That's our advantage, not your problem.",
      intro: "DealerSocket-class systems are overkill and overpriced for a 30-car lot: so most independents run on spreadsheets and Marketplace. Frayze builds you a real inventory tool, syndication, and follow-up: the system a small dealer could never otherwise afford.",
      features: [
        { title: "Custom inventory tool", description: "A real back-end to manage stock: no DMS required, no per-seat enterprise contract." },
        { title: "VIN-decode intake", description: "Scan or type a VIN, auto-fill year/make/model/trim, optimize photos in the browser, publish." },
        { title: "Everywhere syndication", description: "One entry pushes the unit to AutoTrader, CarGurus, Kijiji Autos and Facebook Marketplace." },
        { title: "Inventory-driven social", description: "New units and price drops auto-post to your social: merchandising that runs itself." },
        { title: "SEO vehicle pages", description: "Every unit gets a fast, search-friendly detail page that ranks for 'used {make} {model} {city}'." },
        { title: "Built-in deal desk & lead magnets", description: "Payment calculators, trade-in & affordability tools, and an F&I deal desk: capture and close in one place." },
      ],
      proof: "Live today at The Motoring District (Greater Sudbury): a full inventory + deal-desk + lead-magnet system built for an independent with no DMS.",
      ctaLabel: "Book a dealer walkthrough",
      ctaHref: AUTOMOTIVE_BOOKING_URL,
    },
    websiteSeo: {
      eyebrow: "Websites & used-car SEO",
      heading: "We don't just run your leads: we build the site they land on.",
      intro: "The lead system is only as good as the site feeding it. Whether you own your site or you're locked into an OEM platform, Frayze builds the pages that rank, capture, and convert used-car buyers.",
      tracks: [
        {
          badge: "Independent dealers",
          title: "Your whole site, built to sell",
          tagline: "Fast, SEO-first, inventory baked in.",
          points: [
            "Modern dealer site built for speed and Google",
            "Inventory engine + ranking vehicle detail pages baked in",
            "Lead capture on every page (trade, finance, test drive)",
            "Owned and managed by us: no DIY, no vendor juggling",
          ],
        },
        {
          badge: "Franchise / OEM dealers",
          title: "We manage your used-car sites",
          tagline: "Keep your OEM site untouched. Win the used-car search anyway.",
          points: [
            "Independent 'Used Cars in {City}' authority sites that rank where your OEM site can't",
            "100% OEM-compliant and non-invasive: we never touch your franchise site",
            "Capture the used-car lead and feed it straight into your CRM",
            "Owned lead magnets (credit, trade, payment tools) on assets you control",
          ],
          footnote: "Your used inventory is where the margin and the search freedom live. We own that lane for you.",
        },
      ],
      proof: "Before Frayze, at a 6-rooftop Ontario auto group, we rebuilt the site in-house and ran it to 600–700% monthly traffic: syndicated to AutoTrader, CarGurus, Kijiji and Google Merchant: plus dedicated used-car authority sites that dominated local organic.",
    },
    tiers: {
      eyebrow: "What you can turn on",
      heading: "Three tiers. Turn on what you need, scale as you grow.",
      intro: "No rigid packages. Every dealer starts where it makes sense and adds the rest as they grow: these are the capabilities each tier turns on.",
      tiers: [
        {
          name: "Core",
          tagline: "Capture & respond",
          turnsOn: [
            "Dealer website + ranking vehicle pages",
            "Inventory tool or DMS feed",
            "Syndication to AutoTrader, CarGurus, Kijiji & Marketplace",
            "Instant lead responders on every channel",
            "Missed-call text-back",
            "Reviews & reputation engine",
            "Basic social posting",
            "Database reactivation",
          ],
        },
        {
          name: "Growth",
          tagline: "Automate & convert",
          carriesOver: "Everything in Core, plus:",
          highlight: true,
          turnsOn: [
            "Full lead-flow automation & CRM pipeline",
            "Long-term nurture + dead-deal reactivation",
            "Integration stack (AutoCorp, AutoVerify, Carfax)",
            "AI chat that qualifies & books",
            "Reputation management",
            "Basic attribution & reporting",
          ],
        },
        {
          name: "Group / Authority",
          tagline: "Scale & attribute",
          carriesOver: "Everything in Growth, plus:",
          turnsOn: [
            "Google & Meta ad management",
            "Full ad-to-sold attribution",
            "AI voice agent, 24/7",
            "Multi-rooftop & shared inventory",
            "Dedicated account manager",
            "Custom integrations",
          ],
        },
      ],
      note: "Usage (calls, texts, AI) is billed at cost. The onboarding build is scoped to your store: pricing comes on a quick call once we know your rooftop(s).",
      ctaLabel: "Book a scoping call",
      ctaHref: AUTOMOTIVE_BOOKING_URL,
    },
    serviceSchemaName: "GTA automotive dealer communication and booking automation",
  },  marine: {
    key: "marine",
    slug: "/automotive/",
    meta: {
      title: "Marine Dealer CRM & AI Follow-Up | Frayze Automotive",
      description:
        "Capture every seasonal lead with instant follow-up, storage renewals, and service reminders across your showroom, docks, and storage.",
      canonical: "https://frayze.ca/automotive/",
      ogTitle: "Marine Sales Automation Built for Seasonal Demand | Frayze",
      ogDescription:
        "Launch AI follow-up, storage renewals, and service reminders tailored for marinas, boat dealers, and marine service centers.",
      keywords: "marine crm software, boat dealer software, marina management system, marine service software",
    },
    hero: {
      eyebrow: "Marine",
      title: "Seasonal sales & service automation for marine dealers and marinas",
      description:
        "Marine revenue lives and dies by timing. Frayze automates the follow-up that captures seasonal demand, fills storage, and books service.",
      supporting: "3× faster reply times • Higher winterization & storage conversions • Full visibility across sales + service",
      audienceBullets: ["Boat dealers", "Marinas", "Service shops", "Seasonal storage"],
      primaryCta: "See it in action",
      secondaryCta: "Book Marine Consult",
    },
    experience: {
      heading: "Where marine dealers lose revenue",
      subheading: "Marine demand is predictable. Most dealers just don't have a system to capture it.",
      intro: "Missed storage and winterization inquiries plus slow follow-up in peak months quietly drain revenue.",
      bullets: [
        "Missed storage or winterization inquiries",
        "Slow follow-up during peak months",
        "No-show service appointments",
        "Inconsistent seasonal outreach",
        "No automation for pickup/drop-off",
        "Disconnected inboxes",
        "Poor communication during repairs",
        "Low off-season engagement"
      ],
      stats: [
        {
          value: "3x",
          label: "Faster Replies",
          description: "Reply times."
        },
        {
          value: "High",
          label: "Conversions",
          description: "Higher winterization & storage conversions."
        },
        {
          value: "100%",
          label: "Visibility",
          description: "Full visibility across sales + service."
        }
      ]
    },
    systemsIntro: {
      eyebrow: "The 4 Marine Systems",
      title: "Automate your marine operations.",
      description: "Capture seasonal demand automatically, all year round."
    },
    systems: [
      {
        id: "marine-crm",
        title: "Marine CRM Growth System",
        tagline: "Unified Marine Inbox. Website forms, SMS, calls, storage requests, service inquiries → one place.",
        description: "Includes:",
        included: [
          {
            title: "Seasonal Playbooks",
            description: "Winterization → Storage → Spring Prep, Off-season nurturing, Service reminders, Accessory upsells."
          },
          {
            title: "Service + Sales Pipelines",
            description: "Track every boat, every service, every inquiry."
          },
          {
            title: "Customer lifecycle automation",
            description: "Winter storage confirmations, Pickup reminders, Repair-status alerts, Follow-up for upgrades or trade-ins."
          }
        ],
        outcome: "Stop losing customers to bad timing and missed follow-up."
      },
      {
        id: "marine-lead-gen",
        title: "Marine Lead Gen System",
        tagline: "Seasonal Ad Campaigns. Winterization, storage, spring tune-ups, detailing, boat repair.",
        description: "Includes:",
        included: [
          {
            title: "Lead capture funnels",
            description: "Built specifically for seasonal demand peaks."
          },
          {
            title: "Automated follow-up",
            description: "No staff needed during weekends or after hours."
          },
          {
            title: "Attribution that makes sense",
            description: "Know which campaigns fill your slips."
          }
        ],
        outcome: "Capture every dollar of seasonal demand."
      },
      {
        id: "marine-visibility",
        title: "Marine Visibility System",
        tagline: "Local SEO for marine. Rank for 'boat service', 'winter storage', 'marine repair', etc.",
        description: "Includes:",
        included: [
          {
            title: "Seasonal website optimization",
            description: "Switch landing pages with the season."
          },
          {
            title: "Reputation engine",
            description: "AI responses, CSI prompts, storage reviews."
          }
        ],
        outcome: "Dominate local search for marine services."
      },
      {
        id: "marine-ai",
        title: "Marine AI Intelligence System",
        tagline: "AI concierge. Handles quotes, availability checks, and booking suggestions.",
        description: "Includes:",
        included: [
          {
            title: "AI voice answering",
            description: "Perfect for after-hours spikes."
          },
          {
            title: "AI repair-status assistant",
            description: "Reduces inbound calls."
          },
          {
            title: "AI winterization workflows",
            description: "Explains packages, timelines, and readiness."
          }
        ],
        outcome: "Always-on service, even in the off-season."
      }
    ],
    recommendedRollout: {
      heading: "Marine workflows",
      intro: "Ready-made seasonal automation flows:",
      steps: [
        {
          title: "Winterization Flow",
          description: "Instant response to inquiries → AI explains packages → Automated scheduling → Pickup reminders → Post-service review."
        },
        {
          title: "Storage Flow",
          description: "Automated contract + billing reminders → Availability notifications → Pickup scheduling."
        },
        {
          title: "Spring Prep Flow",
          description: "Automated outreach campaigns → Accessory upsell flows."
        }
      ]
    },
    closingNote: {
      heading: "Ready for the season?",
      description: "Automate your marine operations and capture every lead before your competition does.",
      primaryCtaLabel: "See it in action",
      primaryCtaHref: "/book-consultation/",
      secondaryCtaLabel: "Book Marine Consult",
      secondaryCtaHref: AUTOMOTIVE_BOOKING_URL
    },
    problems: [],
    credibility: "Field-Proven Results",
    credibilityNotes: [],
    credibilityCard: undefined,
    reviewWidget: undefined,
    proof: {
      eyebrow: "Marine Snapshot",
      statement: "Faster reply times on every channel.",
      supporting:
        "Automated communication reduced phone tag and kept customers informed during peak season.",
      linkLabel: "Read Case Study",
      linkHref: "/case-studies/need-a-car-automotive-growth/",
    },
    features: [
      {
        title: "Unified Marine Inbox",
        description: "Route website forms, SMS, calls, storage requests, and service inquiries to one place.",
        href: "/solutions/crm-automation/",
      },
      {
        title: "Seasonal Playbooks",
        description: "Winterization, Storage, Spring Prep, and Off-season nurturing flows.",
        href: "/solutions/lead-generation-system/",
      },
      {
        title: "Service + Sales Pipelines",
        description: "Track every boat, every service, every inquiry.",
        href: "/automotive/",
      },
      {
        title: "Reputation Engine",
        description: "AI responses, CSI prompts, storage reviews.",
        href: "/solutions/lead-generation-system/",
      },
    ],
    offers: [
      {
        ...baseOffers[0],
        description:
          "CRM supplement for marinas and boat dealers that need instant follow-up without ripping out systems.",
        valueProps: [
          "Shared inbox for phones, chat, and web forms",
          "AI books service appointments with reminders",
          "Seasonal scripts and follow-up cadences baked in",
          "Review automation tied to advisor performance"
        ],
        primaryCtaLabel: "See marine workflow",
        primaryCtaHref: "/automotive/",
        secondaryCtaLabel: "View Pricing",
        secondaryCtaHref: "/pricing/"
      },
      {
        ...baseOffers[1],
        description:
          "Onboard to the Frayze CRM: add nurture, reactivation, reviews, and manager escalations to keep slips full.",
        valueProps: [
          "Segmented nurture by boat type or season",
          "Storage renewal flows",
          "Dashboards for service and sales",
          "AI-generated replies that keep tone on-brand"
        ],
        primaryCtaLabel: "Compare packages",
        primaryCtaHref: "/pricing/",
      },
    ],
    faq: [],
    stickyCta: {
      heading: "See it in action",
      secondary: "Book Marine Consult"
    },
    demo: {
      url: BASE_DEMO_URL,
      heading: "See how Frayze automates seasonal marine demand.",
      description: "Watch the workflow from inquiry to booked service."
    },
    serviceSchemaName: "Marine dealer communication and booking automation"
  },

  powerSports: {
    key: "powerSports",
    slug: "/automotive/",
    meta: {
      title: "Power Sports CRM & AI Follow-Up | Frayze Automotive",
      description:
        "Reply to every lead in seconds and book more test rides. AI follow-up and service reminders built for ATV, sled, motorcycle, and motorsports dealers.",
      canonical: "https://frayze.ca/automotive/",
      ogTitle: "Power Sports Sales Automation | Frayze",
      ogDescription:
        "Turn marketplace chats, trade-ins, and service leads into booked rides with AI follow-up built for power sports.",
      keywords: "powersports crm, motorcycle dealer software, atv dealer management system",
    },
    hero: {
      eyebrow: "Power Sports",
      title: "Keep riders engaged from first ping to pick-up.",
      description:
        "Frayze unifies marketplace chats, SMS, and phone calls so your floor books more test rides, accessories, and service without chasing a single lead.",
      supporting: "Works for single-location power sports stores, regional groups, and seasonal rental operations.",
      audienceBullets: ["Sales managers", "Service coordinators", "Accessories & parts teams"],
      primaryCta: "See the power sports workflow",
      secondaryCta: "Book a 20-min consult",
    },
    credibility: "Trusted by motorsports dealers across Canada • Integrates with marketplace listings, rental funnels, and service calendars",
    problems: [
      {
        problem: "Marketplace leads go cold fast",
        solution: "AI replies in seconds",
        description:
          "AI instantly answers availability, trade values, and financing questions before riders move to the next listing."
      },
      {
        problem: "Test rides and service slots get missed",
        solution: "Automated scheduling",
        description:
          "Offer real-time slots, send automatic reminders, and route riders to the right specialist with no no-shows."
      },
      {
        problem: "No visibility into accessory upsell",
        solution: "Pipeline + dashboards",
        description:
          "Track deals, service upsell, and add-on revenue with clear dashboards for leadership."
      }
    ],
    features: [
      {
        title: "Unified power sports inbox",
        description: "Route Meta, Kijiji, website forms, and phone leads to the right salesperson in one queue.",
        href: "/solutions/crm-automation/",
      },
      {
        title: "AI trade & finance follow-up",
        description: "Capture trade details, financing interest, and preferred ride times automatically.",
        href: "/solutions/crm-automation/",
      },
      {
        title: "Service + accessory automation",
        description: "Automated reminders for seasonal service, accessory bundles, and gear upsell.",
        href: "/solutions/lead-generation-system/",
      },
      {
        title: "Review + community engine",
        description: "Collect reviews and social proof after rides, tours, and service appointments.",
        href: "/solutions/lead-generation-system/",
      }
    ],
    offers: [
      {
        ...baseOffers[0],
        description:
          "Launch AI follow-up, test-ride booking, and review automation in a week. Perfect for single-rooftop power sports dealers.",
        valueProps: [
          "Instant responses for Meta Marketplace and web leads",
          "Test-ride calendar with automated reminders",
          "Trade intake forms synced to your pipeline",
          "Accessory and gear reminders post-sale"
        ],
        primaryCtaLabel: "See power sports workflow",
        primaryCtaHref: "/automotive/",
        secondaryCtaLabel: "View Pricing",
        secondaryCtaHref: "/pricing/"
      },
      {
        ...baseOffers[1],
        description:
          "Add nurture sequences, reactivation flows, and service automation to keep riders engaged all season.",
        valueProps: [
          "Segmented nurture by vehicle type and season",
          "Finance and protection plan follow-up",
          "Dashboards for sales, service, and accessories",
          "AI-generated replies that keep tone on-brand"
        ],
        primaryCtaLabel: "Compare packages",
        primaryCtaHref: "/pricing/",
      },
      {
        ...baseOffers[2],
        description:
          "Give ownership and regional managers the visibility they need across multiple stores or franchises.",
        valueProps: [
          "Multi-store routing and permissions",
          "Marketing automations for events and demo days",
          "Real-time KPIs for sales, service, and parts",
          "Executive scorecards with ROI attribution"
        ],
        primaryCtaLabel: "Schedule strategy call",
        primaryCtaHref: AUTOMOTIVE_BOOKING_URL,
      }
    ],
    proof: {
      eyebrow: "Dealer Snapshot",
      statement: "Every test-ride inquiry answered and reminded, automatically.",
      supporting:
        "AI text-back and unified dashboards keep sales focused on closing while riders stay informed.",
      linkLabel: "See dealer case studies",
      linkHref: "/case-studies/",
    },
    faq: [
      {
        question: "Can we manage rentals and sales in one pipeline?",
        answer:
          "Yes. Create separate pipelines and automations for rentals, sales, and service with shared reporting."
      },
      {
        question: "Do you integrate with our DMS or inventory provider?",
        answer:
          "We integrate directly or use secure automations to sync inventory, trades, and deal status."
      },
      {
        question: "Can Frayze handle accessory and gear upsell?",
        answer:
          "Automations prompt riders to add accessories, protection plans, and service packages after purchase."
      }
    ],
    stickyCta: {
      heading: "See the power sports automation live",
      secondary: "Want help now? Book a 20-min consult",
    },
    demo: {
      url: BASE_DEMO_URL,
      heading: "See how marketplace leads become booked test rides and service appointments in minutes.",
      description: "Follow the journey from first ping to financed ride with accessory upsell built in.",
    },
    serviceSchemaName: "Power sports dealer communication and booking automation",
  }
};

export const automotiveOffers = baseOffers;
